import React, { useState, useEffect } from 'react';
import { X, Eye, EyeOff } from 'lucide-react';
import { authService } from '../../services/authService';
import { useAuth } from '../../hooks/useAuth';

interface SettingsPanelProps {
    visible: boolean;
    onClose: () => void;
}

const labelStyle: React.CSSProperties = { display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' };
const inputStyle: React.CSSProperties = {
    width: '100%', padding: '6px 8px', border: '1px solid #d9d9d9',
    borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box',
};
const sectionTitleStyle: React.CSSProperties = {
    fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '10px',
    paddingBottom: '6px', borderBottom: '1px solid #f0f0f0',
};

/**
 * 设置面板：账号安全（修改密码）。
 * 运行状态已移至侧边栏 StatusPopover（点击网络状态图标查看）。
 */
const SettingsPanel: React.FC<SettingsPanelProps> = ({ visible, onClose }) => {
    const { accessToken } = useAuth();

    // 修改密码表单
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPasswords, setShowPasswords] = useState(false);
    const [pwSaving, setPwSaving] = useState(false);
    const [pwMessage, setPwMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    useEffect(() => {
        if (visible) {
            setPwMessage(null);
        } else {
            // 关闭时清空密码输入，避免残留
            setOldPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setPwMessage(null);
        }
    }, [visible]);

    if (!visible) return null;

    const handleChangePassword = async () => {
        setPwMessage(null);
        if (!oldPassword) {
            setPwMessage({ type: 'error', text: '请输入原密码' });
            return;
        }
        if (newPassword.length < 6) {
            setPwMessage({ type: 'error', text: '新密码长度至少为 6 位' });
            return;
        }
        if (/^\d+$/.test(newPassword)) {
            setPwMessage({ type: 'error', text: '新密码不能为纯数字' });
            return;
        }
        if (newPassword !== confirmPassword) {
            setPwMessage({ type: 'error', text: '两次输入的新密码不一致' });
            return;
        }
        if (!accessToken) {
            setPwMessage({ type: 'error', text: '登录状态已失效，请重新登录' });
            return;
        }

        setPwSaving(true);
        try {
            await authService.changePassword(oldPassword, newPassword, accessToken);
            setPwMessage({ type: 'success', text: '密码修改成功' });
            setOldPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (e) {
            setPwMessage({ type: 'error', text: e instanceof Error ? e.message : '修改失败，请稍后重试' });
        } finally {
            setPwSaving(false);
        }
    };

    return (
        <div style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1500,
            display: 'flex', justifyContent: 'center', alignItems: 'center',
        }} onClick={onClose}>
            <div style={{
                backgroundColor: 'white', borderRadius: '12px', width: '92%', maxWidth: '520px',
                maxHeight: '85vh', overflowY: 'auto', padding: '20px 24px',
            }} onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h2 style={{ margin: 0, fontSize: '16px' }}>系统设置</h2>
                    <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#999' }}>
                        <X size={20} />
                    </button>
                </div>

                {/* 账号安全：修改密码 */}
                <div>
                    <div style={sectionTitleStyle}>账号安全</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div>
                            <label style={labelStyle}>原密码</label>
                            <input
                                type={showPasswords ? 'text' : 'password'}
                                value={oldPassword}
                                onChange={e => setOldPassword(e.target.value)}
                                placeholder="请输入当前密码"
                                autoComplete="current-password"
                                style={inputStyle}
                            />
                        </div>
                        <div>
                            <label style={labelStyle}>新密码（至少 6 位，不能为纯数字）</label>
                            <input
                                type={showPasswords ? 'text' : 'password'}
                                value={newPassword}
                                onChange={e => setNewPassword(e.target.value)}
                                placeholder="请输入新密码"
                                autoComplete="new-password"
                                style={inputStyle}
                            />
                        </div>
                        <div>
                            <label style={labelStyle}>确认新密码</label>
                            <input
                                type={showPasswords ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={e => setConfirmPassword(e.target.value)}
                                placeholder="请再次输入新密码"
                                autoComplete="new-password"
                                style={inputStyle}
                            />
                        </div>

                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#666', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={showPasswords}
                                onChange={e => setShowPasswords(e.target.checked)}
                            />
                            显示密码
                            {showPasswords ? <Eye size={13} /> : <EyeOff size={13} />}
                        </label>

                        {pwMessage && (
                            <div style={{ fontSize: '12px', color: pwMessage.type === 'success' ? '#389e0d' : '#cf1322' }}>
                                {pwMessage.text}
                            </div>
                        )}

                        <button
                            onClick={handleChangePassword}
                            disabled={pwSaving}
                            style={{
                                backgroundColor: pwSaving ? '#d9d9d9' : '#1677ff', color: 'white',
                                border: 'none', borderRadius: '6px', padding: '8px 0',
                                cursor: pwSaving ? 'not-allowed' : 'pointer', fontSize: '14px',
                            }}
                        >
                            {pwSaving ? '提交中...' : '修改密码'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SettingsPanel;
